import { Fragment } from 'react';
import { Pill } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { useDrawer } from '@/app/providers';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type {
  AccountingPeriod,
  ChainCheckpointRead,
  ChainGap,
} from '@/versions/v1/schema/metal-accounting';
import {
  allChainReads,
  formatMeasuredQuantity,
  formatSignedGapPct,
  gapTitle,
  type PeriodView,
} from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';

// UI (Layer 4) for ma.D1 / #120 — mine to shipped reconciliation card.
// Pure derivation (chainRead / allChainReads / formatSignedGapPct / gapTitle)
// lives in MetalAccountingLayer.transform.ts; this file only renders it. Sits
// between the process graph and the node detail on /metal-accounting.
//
// Fix round 1 (reviews/ma-recon.md round 1): one flat grid for header + rows
// so columns resolve once (MR-F2); content-sized checkpoint columns so the
// gap pill centres between the two cells it relates, no dead band (MR-F12);
// honest '\u2014' + no pill under shift or when a checkpoint is unmeasured (MR-F3, extended for MTD's mine/shipped rows); checkpoint decimals
// fixed by kind, not element (MR-F5); drawer carries the checkpoint's own
// measurement, not an unrelated case (MR-F1).
//
// ma-stream round 2 (ruling A, MF-1/MF-2/SF-6): the tails-loss row this card
// used to render (paul-fmea.A10 / #275) is deleted. The closure strip above
// this card already carries the same three cells on `/metal-accounting`
// (`ProcessGraph.tsx` -> `MetalAccountingStrip`), so the row was a second
// rendering of one fact, not a second fact \u2014 and it sat directly beneath the
// MILL checkpoint row, implying two different mill
// feeds for the same day. `/plant-model?layer=ma` already carried the three
// cells once, on the strip only; this card now matches that pattern.

// polish.P9 (#657): the card is now the Mine to shipped tab's body, so its own
// frame and title go (the tab names it), and the three gap sentences under the
// grid leave the glass: each restated its pill in words, the same words the
// pill's tooltip (`gapTitle`) already carries (locked comp content move 9).

// One flat grid \u2014 header cells and every element row's cells are direct
// children in the same grid, so all eight columns resolve against one another
// instead of two grids that only share a template string (MR-F2).
//
// Fix round 2 MR-F19: round 1 made the checkpoint columns `max-content` and
// the grid `w-fit`, which centred each pill in the gap between its pair
// (MR-F12, still true) but left the grid narrower than the `GlassCard`
// around it, so the dead band moved from between columns to beside the
// grid (497px on a 1119px card). The two gap-pill columns are now `1fr`
// and the grid drops `w-fit`, so it spans the card's content width and
// each pill centres in the live space between its pair -- the void the
// round-1 fix closed stays closed because it is now real track width the
// pill sits inside, not leftover space past a content-sized grid.
//
// K17 round 1 (#663): four custody points \u2014 Mine \u00b7 Mill (fresh feed) \u00b7
// Mill concentrate (feed \u00d7 recovery) \u00b7 Shipped. The mill \u2192 shipped leg now
// compares the shipment against the concentrate the mill produced, not against
// mill feed metal, so the card adds the `Mill concentrate` column and the
// pill between it and Shipped. The feed \u2192 concentrate step between the two
// mill columns is the plant's recovery (carried on the closure strip, not a
// chain gap), so its slot holds a plain flow arrow, no pill.
const GRID_COLS =
  'grid-cols-[auto_max-content_1fr_max-content_auto_max-content_1fr_max-content]';
const GRID_SPAN = 'col-span-8';

const MINE_PROVENANCE_FULL = '[syn] mine survey claim \u2014 no site source';
const MINE_PROVENANCE_SHORT = '[syn] mine survey claim';
const SHIPPED_PROVENANCE_FULL = '[syn] concentrate despatch \u2014 no site source';
const SHIPPED_PROVENANCE_SHORT = '[syn] concentrate despatch';
// Fix round 2 MR-F15 gave Mill a fabricated `(seed)` provenance chip so its
// one-line header cell would match the two-line Mine/Shipped cells under
// `items-center` -- a new defect (MR-F24, round 3): a token with no
// definition anywhere in this product, visually indistinguishable from the
// two honest `[syn]` chips beside it, implying the mill column is as soft as
// the two synthetic ones when `reconciliation.md` says the opposite. Only
// Mine and Shipped are synthetic and carry a provenance chip; Mill carries
// none. Fixed in the layout layer instead: each header cell is `self-start`
// so it aligns to the row's top regardless of its own height, putting all
// three labels on one line with no content added.

// Grade decimals are a checkpoint-kind convention (mine/mill design-basis
// figures carry two decimals; concentrate assays carry one), not an
// element-kind one \u2014 fixes the ragged column MR-F5 flagged. K17 round 1
// (#663): `mill-concentrate` is a concentrate assay, so it takes one decimal
// like `shipped`.
function gradeDecimalsForCheckpoint(id: ChainCheckpointRead['id']): number {
  return id === 'shipped' || id === 'mill-concentrate' ? 1 : 2;
}

const CONTAINED_DECIMALS: Record<'Zn' | 'Pb' | 'Ag', number> = { Zn: 2, Pb: 2, Ag: 1 };

function CheckpointCell({
  checkpoint,
  element,
  period,
  onOpen,
}: {
  checkpoint: ChainCheckpointRead;
  element: 'Zn' | 'Pb' | 'Ag';
  period: AccountingPeriod;
  onOpen: () => void;
}) {
  const containedLabel = checkpoint.contained
    ? formatMeasuredQuantity(checkpoint.contained, period, { decimals: CONTAINED_DECIMALS[element] })
    : '\u2014';
  const subLabel =
    checkpoint.mass && checkpoint.grade
      ? `${formatMeasuredQuantity(checkpoint.mass)} \u00d7 ${formatMeasuredQuantity(checkpoint.grade, undefined, {
          decimals: gradeDecimalsForCheckpoint(checkpoint.id),
        })}`
      : '';

  return (
    <button
      type="button"
      onClick={onOpen}
      title={`${checkpoint.source} (${checkpoint.provenance})`}
      className="flex min-w-0 flex-col items-start gap-0 rounded-lg px-2 py-0.5 text-left hover:bg-[var(--glass-surface-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
    >
      <span className="text-sm font-semibold tabular tracking-tight text-zinc-900 dark:text-zinc-100">
        {containedLabel}
      </span>
      {subLabel ? <span className="text-[10px] text-muted">{subLabel}</span> : null}
    </button>
  );
}

function GapPill({ gap, element }: { gap: ChainGap; element: 'Zn' | 'Pb' | 'Ag' }) {
  // K17 round 3 (#663): the mill concentrate → shipped leg at WTD / MTD is a
  // stock position, not a gap — the neutral, untoned share the Period close
  // prints. It sits in the same column the toned gap pill takes at `day`, so
  // the two reads occupy one slot and never both render.
  if (gap.stock) {
    return (
      <div
        className="flex flex-col items-center gap-0.5 justify-self-center text-center"
        title={gap.stock.title}
        data-chain-stock={gap.stock.kind}
      >
        <Pill tone="default" className="shrink-0 whitespace-nowrap" title={gap.stock.title}>
          {gap.stock.label}
        </Pill>
        <span className="whitespace-nowrap text-[10px] text-muted">
          {gap.stock.mass} · {gap.stock.share}
        </span>
      </div>
    );
  }
  // Q6 / fix round 1 MR-F3: whenever a checkpoint has no contained-metal figure (shift; MTD's mine and shipped rows) there is no gap pill at all \u2014
  // an empty grid cell holds the column, nothing decorative fills it.
  if (gap.pct === null) return <span />;
  const tone =
    gap.tone === 'ok'
      ? 'success'
      : gap.tone === 'warn'
        ? 'warning'
        : gap.tone === 'bad'
          ? 'danger'
          : 'default';
  return (
    <Pill tone={tone} className="shrink-0 justify-self-center whitespace-nowrap" title={gapTitle(gap, element)}>
      {formatSignedGapPct(gap.pct)}
    </Pill>
  );
}

/** K17 round 3 (#663): the mill's feed → concentrate step is recovery, so the
 *  arrow between the Mill and Mill concentrate columns carries the recovery
 *  figure the KPI row and the closure strip read — a number that explains the
 *  mass drop, not a sentence. komatsu.F11 (#737): the figure rides the arrow,
 *  both on the row's centre line. Stacked under the glyph the figure dropped
 *  13.5 px to the checkpoint cells' sub-line and read as that cell's caption. */
function RecoveryArrow({ recoveryPct }: { recoveryPct: number | null }) {
  return (
    <span
      data-chain-recovery-arrow
      className="flex items-center justify-self-center gap-1 leading-tight"
      title={
        recoveryPct === null
          ? 'Mill feed → concentrate: no recovery reading for this period.'
          : `${recoveryPct.toFixed(1)} % of the feed metal recovered into concentrate — the mill's feed → concentrate step.`
      }
    >
      <span aria-hidden data-chain-recovery-glyph className="text-xs text-muted">
        {'\u2192'}
      </span>
      {recoveryPct !== null ? (
        <span data-chain-recovery-label className="whitespace-nowrap text-[10px] text-muted">
          {recoveryPct.toFixed(1)} %
        </span>
      ) : null}
    </span>
  );
}

export function MineToShippedCard({ view }: { view: PeriodView }) {
  const { open: openDrawer } = useDrawer();
  const reads = allChainReads(view);

  // Fix round 2 MR-F20: the cell formats grade with
  // `gradeDecimalsForCheckpoint()` and contained metal with
  // `CONTAINED_DECIMALS[element]`; the drawer this opens now uses the same
  // two functions on the same values, so "5.40% Zn" on the glass never
  // opens a drawer reading "5.4% Zn" for the same number.
  const openCheckpointDrawer = (checkpoint: ChainCheckpointRead, element: 'Zn' | 'Pb' | 'Ag') => {
    const synthetic = checkpoint.provenance.trim().startsWith('[syn]');
    openDrawer('case-drawer', {
      kicker: 'Reconciliation',
      title: `${checkpoint.label} \u00b7 ${element}`,
      data: {
        measurement: {
          checkpointLabel: checkpoint.label,
          element,
          mass: checkpoint.mass ? formatMeasuredQuantity(checkpoint.mass) : '\u2014',
          grade: checkpoint.grade
            ? formatMeasuredQuantity(checkpoint.grade, undefined, {
                decimals: gradeDecimalsForCheckpoint(checkpoint.id),
              })
            : '\u2014',
          contained: checkpoint.contained
            ? formatMeasuredQuantity(checkpoint.contained, view.period, { decimals: CONTAINED_DECIMALS[element] })
            : '\u2014',
          source: checkpoint.source,
          provenance: checkpoint.provenance,
          synthetic,
        },
      },
    });
  };

  // Fix round 1 MR-F7: one string, owned by the transform (`ChainRead.note`);
  // the component never duplicates it, and it renders only when the period
  // actually carries the honest-state note (never on `day`).
  const noteLine = reads[0]?.note ?? null;

  return (
    <Commentable name="MineToShipped">
      <div className="space-y-1.5" data-mine-to-shipped>
          <div className={`grid ${GRID_COLS} items-center gap-x-2 gap-y-1.5`}>
            {/* Header row \u2014 flat children of the same grid as the element
                rows below, so all eight columns resolve together (MR-F2). */}
            <span />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 self-start">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted">Mine</span>
              <ProvenanceChip text={MINE_PROVENANCE_SHORT} title={MINE_PROVENANCE_FULL} full />
            </div>
            <span />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 self-start">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted">Mill</span>
            </div>
            <span />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 self-start">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted">Mill concentrate</span>
            </div>
            <span />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 self-start">
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted">Shipped</span>
              <ProvenanceChip text={SHIPPED_PROVENANCE_SHORT} title={SHIPPED_PROVENANCE_FULL} full />
            </div>

            {/* polish.P9: a hairline under the header and between element
                rows, spanning the grid (comp `.rowline`). */}
            <span aria-hidden className={`${GRID_SPAN} h-px bg-[var(--color-divider)]`} />
            {reads.map((read, index) => {
              const [mine, mill, millConcentrate, shipped] = read.checkpoints;
              const [mineToMill, millConcentrateToShipped] = read.gaps;
              return (
                <Fragment key={read.element}>
                  <span className="text-xs font-semibold text-muted">{read.element}</span>
                  <CheckpointCell
                    checkpoint={mine!}
                    element={read.element}
                    period={view.period}
                    onOpen={() => openCheckpointDrawer(mine!, read.element)}
                  />
                  <GapPill gap={mineToMill!} element={read.element} />
                  <CheckpointCell
                    checkpoint={mill!}
                    element={read.element}
                    period={view.period}
                    onOpen={() => openCheckpointDrawer(mill!, read.element)}
                  />
                  {/* Feed → concentrate is the mill's recovery, not a chain
                      gap — the arrow carries the recovery figure (K17 r3). */}
                  <RecoveryArrow recoveryPct={read.recoveryPct} />
                  <CheckpointCell
                    checkpoint={millConcentrate!}
                    element={read.element}
                    period={view.period}
                    onOpen={() => openCheckpointDrawer(millConcentrate!, read.element)}
                  />
                  <GapPill gap={millConcentrateToShipped!} element={read.element} />
                  <CheckpointCell
                    checkpoint={shipped!}
                    element={read.element}
                    period={view.period}
                    onOpen={() => openCheckpointDrawer(shipped!, read.element)}
                  />
                  {index < reads.length - 1 ? (
                    <span aria-hidden className={`${GRID_SPAN} h-px bg-[var(--color-divider)]`} />
                  ) : null}
                </Fragment>
              );
            })}
          </div>
          {noteLine ? <p className="pt-1 text-xs text-muted">{noteLine}</p> : null}
      </div>
    </Commentable>
  );
}
